import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import { useToast } from '../../context/ToastContext';
import { expensesAPI, projectsAPI, usersAPI } from '../../services/api';
import { Expense, EXPENSE_CATEGORIES } from '../../types/expense';
import RejectExpenseModal from '../expense/RejectExpenseModal';
import {
  CheckIcon,
  XIcon,
  ReceiptScallopedIcon,
  SearchIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '../icons';

// Icons matching TimeOffManagement 1.5px stroke style
const Icons = {
  check: <CheckIcon className="" width={16} height={16} strokeWidth={1.5} />,
  x: <XIcon className="" width={16} height={16} strokeWidth={1.5} />,
  receipt: <ReceiptScallopedIcon className="" width={20} height={20} strokeWidth={1.5} />,
  search: <SearchIcon className="" width={16} height={16} strokeWidth={1.5} />,
  chevronDown: <ChevronDownIcon className="" width={16} height={16} strokeWidth={1.5} />,
  chevronRight: <ChevronRightIcon className="" width={18} height={18} strokeWidth={1.5} />,
};

interface Project {
  id: number;
  name: string;
}

interface User {
  id: number;
  full_name: string;
  email: string;
}

const ExpenseApprovalTable: React.FC = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { showToast } = useToast();

  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [approvingId, setApprovingId] = useState<number | null>(null);
  const [rejectExpense, setRejectExpense] = useState<Expense | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // Filter state
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Filter options
  const [projects, setProjects] = useState<Project[]>([]);
  const [users, setUsers] = useState<User[]>([]);

  // Theme classes matching TimeOffManagement patterns
  const cardClass = isDark
    ? 'bg-slate-900/50 border border-slate-700/50 backdrop-blur-sm'
    : 'bg-white border border-slate-200 shadow-sm';

  const inputClass = isDark
    ? 'px-3 py-2 bg-slate-900/50 border border-slate-700 text-white placeholder-slate-500 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors text-sm'
    : 'px-3 py-2 bg-white border border-slate-200 text-slate-900 placeholder-slate-400 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors text-sm';

  const selectClass = isDark
    ? 'px-3 py-2 bg-slate-900/50 border border-slate-700 text-white rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors text-sm'
    : 'px-3 py-2 bg-white border border-slate-200 text-slate-900 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors text-sm';

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {
        statuses: 'pending,approved,rejected'
      };
      if (dateFrom) params.from = dateFrom;
      if (dateTo) params.to = dateTo;
      if (employeeFilter) params.user_id = employeeFilter;
      if (projectFilter) params.project_id = projectFilter;
      if (categoryFilter) params.category = categoryFilter;

      const response = await expensesAPI.getPending(params);
      setExpenses(response.data.expenses || response.data || []);
    } catch {
      showToast(t('expense.loadFailed', 'Failed to load expenses'), 'error');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, employeeFilter, projectFilter, categoryFilter, showToast, t]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  useEffect(() => {
    Promise.all([projectsAPI.getAll(), usersAPI.getAll()])
      .then(([projRes, userRes]) => {
        setProjects(projRes.data.projects || projRes.data || []);
        setUsers(userRes.data.users || userRes.data || []);
      })
      .catch(() => {
        // Non-critical: filters still work without option lists
      });
  }, []);

  const handleApprove = async (expense: Expense) => {
    setApprovingId(expense.id);
    try {
      await expensesAPI.approve(expense.id);
      showToast(t('expense.approveSuccess', 'Expense approved successfully'), 'success');
      fetchExpenses();
    } catch {
      showToast(t('expense.approveFailed', 'Failed to approve expense'), 'error');
    } finally {
      setApprovingId(null);
    }
  };

  const formatCurrency = (amount: string, currency: string) =>
    `${currency} ${parseFloat(amount).toFixed(2)}`;

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString();

  const getCategoryLabel = (value: string) =>
    EXPENSE_CATEGORIES.find((c) => c.value === value)?.label ?? value;

  // Skeleton rows for loading state
  if (loading) {
    return (
      <div className={`${cardClass} rounded-2xl overflow-hidden`}>
        <div className="animate-pulse">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className={`h-16 border-b ${isDark ? 'border-slate-700/50 bg-slate-800/30' : 'border-slate-100 bg-slate-50'}`}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Reject modal */}
      {rejectExpense && (
        <RejectExpenseModal
          isOpen={true}
          expense={rejectExpense}
          onClose={() => setRejectExpense(null)}
          onSuccess={() => {
            setRejectExpense(null);
            fetchExpenses();
          }}
        />
      )}

      {/* Filters */}
      <div className={`${cardClass} rounded-2xl p-4 mb-4`}>
        <div className="flex flex-wrap gap-3">
          {/* Employee filter */}
          <select
            value={employeeFilter}
            onChange={(e) => setEmployeeFilter(e.target.value)}
            className={selectClass}
          >
            <option value="">{t('expense.allEmployees', 'All Employees')}</option>
            {users.map((u) => (
              <option key={u.id} value={String(u.id)}>
                {u.full_name}
              </option>
            ))}
          </select>

          {/* Project filter */}
          <select
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            className={selectClass}
          >
            <option value="">{t('expense.allProjects', 'All Projects')}</option>
            {projects.map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.name}
              </option>
            ))}
          </select>

          {/* Category filter */}
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className={selectClass}
          >
            <option value="">{t('expense.allCategories', 'All Categories')}</option>
            {EXPENSE_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>

          {/* Date range */}
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className={inputClass}
            title={t('common.from', 'From')}
          />
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className={inputClass}
            title={t('common.to', 'To')}
          />
        </div>
      </div>

      {/* Table / Empty state */}
      {expenses.length === 0 ? (
        <div
          className={`${cardClass} rounded-2xl p-12 text-center`}
        >
          <div
            className={`w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}
          >
            <span className={isDark ? 'text-slate-600' : 'text-slate-400'}>
              {Icons.receipt}
            </span>
          </div>
          <p className={`font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            {t('expense.noPendingExpenses', 'No pending expenses to review')}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {expenses.map((expense) => {
            const isExpanded = expandedId === expense.id;
            return (
              <div key={expense.id} className={`${cardClass} rounded-2xl overflow-hidden`}>
                {/* Row header — clickable to expand */}
                <div
                  onClick={() => setExpandedId(isExpanded ? null : expense.id)}
                  className={`w-full px-4 sm:px-6 py-4 text-left transition-colors cursor-pointer ${isDark ? 'hover:bg-slate-700/30' : 'hover:bg-slate-50'}`}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={`transition-transform duration-200 flex-shrink-0 ${isExpanded ? 'rotate-90' : ''} ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                    >
                      {Icons.chevronRight}
                    </span>

                    {/* Avatar */}
                    <div className="hidden sm:flex flex-shrink-0 h-10 w-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl items-center justify-center">
                      <span className="text-sm font-bold text-white">
                        {expense.user.full_name
                          .split(' ')
                          .map((n) => n[0])
                          .join('')
                          .substring(0, 2)
                          .toUpperCase()}
                      </span>
                    </div>

                    {/* Main info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                        <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          {expense.user.full_name}
                        </span>
                        <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                          {expense.merchant_name || getCategoryLabel(expense.category)}
                        </span>
                        {expense.project && (
                          <span className={`hidden sm:inline text-xs px-2 py-0.5 rounded-lg ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
                            {expense.project.name}
                          </span>
                        )}
                      </div>
                      <div className={`flex items-center gap-2 mt-0.5 text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                        <span>{formatDate(expense.expense_date)}</span>
                        <span>·</span>
                        <span className={`px-1.5 py-0.5 rounded-md ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
                          {getCategoryLabel(expense.category)}
                        </span>
                        {expense.status === 'approved' && (
                          <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-700'}`}>
                            Approved
                          </span>
                        )}
                        {expense.status === 'rejected' && (
                          <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${isDark ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-700'}`}>
                            Rejected
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Amount */}
                    <div className="flex-shrink-0 text-right">
                      <span className={`text-base font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {formatCurrency(expense.amount, expense.currency)}
                      </span>
                    </div>

                    {/* Action buttons — stop propagation so row click doesn't toggle expand */}
                    <div
                      className="flex-shrink-0 flex items-center gap-2 ml-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        onClick={() => handleApprove(expense)}
                        disabled={approvingId === expense.id}
                        title={t('expense.approve', 'Approve')}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-xl transition-all ${
                          approvingId === expense.id
                            ? 'opacity-50 cursor-not-allowed bg-emerald-600/50 text-white'
                            : 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-sm shadow-emerald-500/20'
                        }`}
                      >
                        {Icons.check}
                        <span className="hidden sm:inline">{t('expense.approve', 'Approve')}</span>
                      </button>
                      <button
                        onClick={() => setRejectExpense(expense)}
                        disabled={approvingId === expense.id}
                        title={t('expense.reject', 'Reject')}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-xl transition-all border ${
                          isDark
                            ? 'border-slate-600 text-slate-300 hover:bg-rose-600 hover:border-rose-600 hover:text-white'
                            : 'border-slate-200 text-slate-600 hover:bg-rose-600 hover:border-rose-600 hover:text-white'
                        } disabled:opacity-50`}
                      >
                        {Icons.x}
                        <span className="hidden sm:inline">{t('expense.reject', 'Reject')}</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Expanded detail */}
                {isExpanded && (
                  <div
                    className={`px-6 pb-5 pt-1 border-t ${isDark ? 'border-slate-700/50' : 'border-slate-100'}`}
                  >
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className={`text-xs font-medium mb-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                          {t('expense.employee', 'Employee')}
                        </p>
                        <p className={isDark ? 'text-white' : 'text-slate-900'}>
                          {expense.user.full_name}
                        </p>
                        <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                          {expense.user.email}
                        </p>
                      </div>
                      <div>
                        <p className={`text-xs font-medium mb-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                          {t('expense.merchant', 'Merchant')}
                        </p>
                        <p className={isDark ? 'text-white' : 'text-slate-900'}>
                          {expense.merchant_name || '—'}
                        </p>
                      </div>
                      <div>
                        <p className={`text-xs font-medium mb-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                          {t('expense.amount', 'Amount')}
                        </p>
                        <p className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          {formatCurrency(expense.amount, expense.currency)}
                        </p>
                      </div>
                      <div>
                        <p className={`text-xs font-medium mb-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                          {t('expense.category', 'Category')}
                        </p>
                        <p className={isDark ? 'text-white' : 'text-slate-900'}>
                          {getCategoryLabel(expense.category)}
                        </p>
                      </div>
                      <div>
                        <p className={`text-xs font-medium mb-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                          {t('expense.date', 'Date')}
                        </p>
                        <p className={isDark ? 'text-white' : 'text-slate-900'}>
                          {formatDate(expense.expense_date)}
                        </p>
                      </div>
                      {expense.project && (
                        <div>
                          <p className={`text-xs font-medium mb-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                            {t('expense.project', 'Project')}
                          </p>
                          <p className={isDark ? 'text-white' : 'text-slate-900'}>
                            {expense.project.name}
                          </p>
                        </div>
                      )}
                      {expense.description && (
                        <div className="col-span-2 sm:col-span-3">
                          <p className={`text-xs font-medium mb-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                            {t('expense.description', 'Description')}
                          </p>
                          <p className={isDark ? 'text-slate-300' : 'text-slate-700'}>
                            {expense.description}
                          </p>
                        </div>
                      )}
                      <div>
                        <p className={`text-xs font-medium mb-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                          {t('expense.billable', 'Billable')}
                        </p>
                        <p className={isDark ? 'text-white' : 'text-slate-900'}>
                          {expense.billable ? t('common.yes', 'Yes') : t('common.no', 'No')}
                        </p>
                      </div>
                      <div>
                        <p className={`text-xs font-medium mb-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                          {t('expense.reimbursable', 'Reimbursable')}
                        </p>
                        <p className={isDark ? 'text-white' : 'text-slate-900'}>
                          {expense.reimbursable ? t('common.yes', 'Yes') : t('common.no', 'No')}
                        </p>
                      </div>
                      {expense.expense_attachments && expense.expense_attachments.length > 0 && (
                        <div className="col-span-2 sm:col-span-3">
                          <p className={`text-xs font-medium mb-3 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                            {t('expense.receipts', 'Receipts')}
                          </p>
                          <div className="grid grid-cols-4 sm:grid-cols-10 gap-3">
                            {expense.expense_attachments.map((att) => {
                              const token = localStorage.getItem('access_token') || '';
                              return (
                              <a
                                key={att.id}
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  const token = localStorage.getItem('access_token');
                                  const url = `${process.env.REACT_APP_API_URL}${att.file_url}`;
                                  fetch(url, {
                                    headers: { 'Authorization': `Bearer ${token}` }
                                  })
                                    .then(res => res.blob())
                                    .then(blob => {
                                      const blobUrl = window.URL.createObjectURL(blob);
                                      const link = document.createElement('a');
                                      link.href = blobUrl;
                                      link.download = att.file_name;
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                      window.URL.revokeObjectURL(blobUrl);
                                    });
                                }}
                                className={`relative group rounded-xl overflow-hidden border-2 transition-all cursor-pointer ${
                                  isDark
                                    ? 'border-slate-600 hover:border-violet-500'
                                    : 'border-slate-200 hover:border-violet-500'
                                }`}
                              >
                                {att.is_image ? (
                                  <>
                                    <img
                                      src={`${process.env.REACT_APP_API_URL}${att.file_url}?token=${token}`}
                                      alt={att.file_name}
                                      className="w-full h-full object-cover"
                                    />
                                    <div
                                      className={`absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center ${
                                        isDark ? '' : ''
                                      }`}
                                    >
                                      <svg
                                        className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                      >
                                        <path d="M21 21H3V3h18v18zm-2-2V5H5v14h14zM8 12a2 2 0 1 0 4 0 2 2 0 0 0-4 0zm6-4h6v6h-6z" />
                                      </svg>
                                    </div>
                                  </>
                                ) : (
                                  <div
                                    className={`w-full h-24 flex items-center justify-center ${
                                      isDark ? 'bg-slate-800' : 'bg-slate-50'
                                    }`}
                                  >
                                    <svg
                                      className={`w-8 h-8 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="1.5"
                                    >
                                      <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
                                      <polyline points="13 2 13 9 20 9" />
                                    </svg>
                                  </div>
                                )}
                              </a>
                            );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

export default ExpenseApprovalTable;
